import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import {
  selectHasModuleAccess,
  selectUserRole,
} from "../../../store/authSlice";
import {
  bulkUploadHotelFeedbackApi,
  deleteHotelFeedbackApi,
  listHotelFeedbackApi,
  listHotelFeedbackMonthsApi,
  saveHotelFeedbackApi,
} from "../../../services/hotelFeedback";
import {
  RATING_OPTIONS,
  RepeatBadge,
  StarRating,
  WHITE,
  downloadMonthExcel,
  downloadTemplate,
  errMsg,
  field,
  fmtDate,
  inr,
  monthLabel,
  panel,
} from "./config";

// Hotel Feedback sheet — works like the Customer Database: the employee uploads
// the guest sheet every time, new rows are added below the earlier ones, and
// the sheet is kept month-wise (by Guest Check-In Date). Only Remark's and the
// stars are editable here; every other column comes from the upload.
const cell = "px-3 py-1.5 text-sm text-gray-800";
const th = "whitespace-nowrap px-3 py-2 text-left font-medium";

const Feedback = () => {
  const canRead = useSelector(selectHasModuleAccess("hotelFeedback", "read"));
  const canWrite = useSelector(
    selectHasModuleAccess("hotelFeedback", "read-write"),
  );
  const isSuper = useSelector(selectUserRole) === "super-admin";

  const [months, setMonths] = useState([]);
  const [month, setMonth] = useState("");
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [rated, setRated] = useState("");
  const [drafts, setDrafts] = useState({}); // id → { remarks?, rating?, negative? }
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const dirtyCount = Object.keys(drafts).length;

  const loadMonths = useCallback(async () => {
    try {
      const { data } = await listHotelFeedbackMonthsApi();
      setMonths(data.months || []);
      return data.months || [];
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Could not load months",
        text: errMsg(err),
      });
      return [];
    }
  }, []);

  const loadRows = useCallback(async (m) => {
    if (!m) {
      setRows([]);
      setStats(null);
      return;
    }
    setLoading(true);
    try {
      const { data } = await listHotelFeedbackApi({ month: m, limit: 3000 });
      setRows(data.items || []);
      setStats(data.stats || null);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Could not load the sheet",
        text: errMsg(err),
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canRead) return;
    loadMonths().then((ms) => {
      if (ms.length) setMonth((cur) => cur || ms[0].month);
    });
  }, [canRead, loadMonths]);

  useEffect(() => {
    setDrafts({});
    loadRows(month);
  }, [month, loadRows]);

  // Warn before leaving the page with unsaved edits.
  useEffect(() => {
    if (!dirtyCount) return undefined;
    const h = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirtyCount]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      const rating =
        drafts[r._id]?.rating !== undefined ? drafts[r._id].rating : r.rating;
      const remarks =
        drafts[r._id]?.remarks !== undefined ? drafts[r._id].remarks : r.remarks;
      // Feedback is done once there are stars OR a remark.
      const done =
        (rating != null && rating !== "") || Boolean(String(remarks || "").trim());
      if (rated === "yes" && !done) return false;
      if (rated === "no" && done) return false;
      if (!needle) return true;
      return [
        r.name,
        r.phone,
        r.room,
        r.roomType,
        r.remarks,
        drafts[r._id]?.remarks,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle));
    });
  }, [rows, q, rated, drafts]);

  // Done / pending for the open month, counted from the rows on screen (with
  // unsaved edits) so it moves the moment a remark or star is entered.
  const liveCounts = useMemo(() => {
    let done = 0;
    rows.forEach((r) => {
      const d = drafts[r._id] || {};
      const rating = d.rating !== undefined ? d.rating : r.rating;
      const remarks = d.remarks !== undefined ? d.remarks : r.remarks;
      if ((rating != null && rating !== "") || String(remarks || "").trim()) done += 1;
    });
    return { done, pending: rows.length - done };
  }, [rows, drafts]);

  if (!canRead) {
    return (
      <>
        <PageTitle activeMenu="Feedback" motherMenu="Hotel Feedback" />
        <div className={`${panel} p-10 text-center`}>
          <h3 className="text-lg font-semibold text-gray-900">
            You don't have access to Hotel Feedback.
          </h3>
        </div>
      </>
    );
  }

  const valueOf = (r, k) =>
    drafts[r._id]?.[k] !== undefined ? drafts[r._id][k] : r[k];

  const edit = (r, k, v) =>
    setDrafts((d) => {
      const patch = { [k]: v };
      // A low rating (2★ or less) ticks "Negative" for you — untick if not.
      if (k === "rating" && v !== "" && Number(v) <= 2) patch.negative = true;
      const next = { ...d, [r._id]: { ...(d[r._id] || {}), ...patch } };
      // drop the draft again if it matches what's saved
      const dr = next[r._id];
      const same =
        (dr.remarks === undefined || dr.remarks === (r.remarks || "")) &&
        (dr.rating === undefined ||
          String(dr.rating ?? "") === String(r.rating ?? "")) &&
        (dr.negative === undefined || dr.negative === Boolean(r.negative));
      if (same) delete next[r._id];
      return next;
    });

  const switchMonth = async (m) => {
    if (m === month) return;
    if (dirtyCount) {
      const ok = await Swal.fire({
        icon: "warning",
        title: "Unsaved changes",
        text: `${dirtyCount} row(s) have unsaved remarks / stars. Discard them?`,
        showCancelButton: true,
        confirmButtonText: "Discard",
      });
      if (!ok.isConfirmed) return;
    }
    setMonth(m);
  };

  // Saves every changed row, or just the one row when `onlyId` is given.
  const save = async (onlyId) => {
    if (!dirtyCount) return;
    const ids = typeof onlyId === "string" ? [onlyId] : Object.keys(drafts);
    setSaving(true);
    try {
      const updates = ids.filter((id) => drafts[id]).map((id) => {
        const d = drafts[id];
        return {
          id,
          ...(d.remarks !== undefined ? { remarks: d.remarks } : {}),
          ...(d.rating !== undefined
            ? { rating: d.rating === "" ? null : Number(d.rating) }
            : {}),
          ...(d.negative !== undefined ? { negative: d.negative } : {}),
        };
      });
      const { data } = await saveHotelFeedbackApi(updates);
      Swal.fire({
        icon: "success",
        title: data.message || "Saved",
        timer: 1200,
        showConfirmButton: false,
      });
      setDrafts((d) => {
        const rest = { ...d };
        ids.forEach((id) => delete rest[id]);
        return rest;
      });
      await Promise.all([loadRows(month), loadMonths()]);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Could not save", text: errMsg(err) });
    } finally {
      setSaving(false);
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    if (dirtyCount) {
      Swal.fire({
        icon: "warning",
        title: "Save your changes first",
        text: "You have unsaved remarks / stars.",
      });
      return;
    }
    setUploading(true);
    try {
      const { data } = await bulkUploadHotelFeedbackApi(file);
      const s = data.summary || {};
      const byMonth = Object.entries(s.byMonth || {})
        .map(([m, n]) => `${monthLabel(m)}: ${n}`)
        .join(", ");
      const issues = (s.errors || [])
        .slice(0, 8)
        .map((er) => `Row ${er.line}: ${er.message}`);
      await Swal.fire({
        icon: "success",
        title: "Sheet uploaded",
        html: `<div style="text-align:left;font-size:14px">${data.message}${
          byMonth ? `<br/><b>Added to</b> ${byMonth}` : ""
        }${
          issues.length
            ? `<div style="margin-top:8px;color:#b45309;font-size:12px">${issues.join("<br/>")}${
                s.errorCount > issues.length
                  ? `<br/>…and ${s.errorCount - issues.length} more`
                  : ""
              }</div>`
            : ""
        }</div>`,
      });
      const ms = await loadMonths();
      const target =
        Object.keys(s.byMonth || {})
          .sort()
          .pop() ||
        month ||
        ms[0]?.month;
      if (target && target !== month) setMonth(target);
      else loadRows(month);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Upload failed", text: errMsg(err) });
    } finally {
      setUploading(false);
    }
  };

  const removeRow = async (r) => {
    const ok = await Swal.fire({
      icon: "warning",
      title: "Delete this row?",
      text: `${r.name}${r.room ? ` · Room ${r.room}` : ""}`,
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#e11d48",
    });
    if (!ok.isConfirmed) return;
    try {
      await deleteHotelFeedbackApi(r._id);
      setDrafts((d) => {
        const n = { ...d };
        delete n[r._id];
        return n;
      });
      await Promise.all([loadRows(month), loadMonths()]);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Could not delete",
        text: errMsg(err),
      });
    }
  };

  return (
    <>
      <PageTitle activeMenu="Feedback" motherMenu="Hotel Feedback" />
      <div className="space-y-6">
        <section className={`${panel} p-6`}>
          <div className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">
            Guest Feedback Sheet
          </div>
          <h1 className="mt-3 text-2xl font-semibold text-gray-900">
            Hotel Feedback
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Upload the guest sheet every time — new guests are added{" "}
            <b>below</b> the earlier ones and filed month-wise by{" "}
            <b>Guest Check-In Date</b>. Guests already uploaded are skipped, so
            their remarks and stars are kept. Only <b>Remark's</b> and{" "}
            <b>Stars</b> can be edited here.
          </p>
        </section>

        <section className={`${panel} p-5 md:p-6`}>
          {/* month tabs */}
          <div className="mb-4 flex flex-wrap gap-2">
            {months.length === 0 && (
              <span className="text-sm text-gray-400">
                No sheet uploaded yet.
              </span>
            )}
            {months.map((m) => (
              <button
                key={m.month}
                type="button"
                onClick={() => switchMonth(m.month)}
                className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition ${
                  m.month === month
                    ? "border-amber-500 bg-amber-500 text-slate-950"
                    : "border-gray-200 bg-white text-gray-600 hover:border-amber-300"
                }`}
              >
                {monthLabel(m.month)}
                <span
                  className={`ml-1.5 ${m.month === month ? "text-slate-800" : "text-gray-400"}`}
                >
                  {m.month === month && rows.length ? liveCounts.done : m.rated}/{m.total}
                </span>
              </button>
            ))}
          </div>

          {/* toolbar */}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <input
                className={`${field} w-56`}
                style={WHITE}
                placeholder="Search name, phone, room…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              <select
                className={`${field} w-36`}
                style={WHITE}
                value={rated}
                onChange={(e) => setRated(e.target.value)}
              >
                <option value="">All rows</option>
                <option value="no">Pending</option>
                <option value="yes">Feedback done</option>
              </select>
              <span className="text-sm text-gray-500">
                {loading
                  ? "Loading…"
                  : stats
                    ? `${stats.total} guests · ${liveCounts.done} done · ${liveCounts.pending} pending`
                    : ""}
              </span>
              {stats?.avgRating != null && (
                <StarRating value={stats.avgRating} size={14} />
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={downloadTemplate}
                className="rounded-full border-2 border-gray-300 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-gray-600 transition hover:bg-gray-50"
              >
                Template
              </button>
              {canWrite && (
                <>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".xlsx,.csv"
                    className="hidden"
                    onChange={upload}
                  />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="rounded-full border-2 border-sky-400 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-sky-700 transition hover:bg-sky-50 disabled:opacity-50"
                  >
                    {uploading ? "Uploading…" : "⬆ Bulk Upload"}
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => downloadMonthExcel(month, rows)}
                disabled={!rows.length}
                className="rounded-full border-2 border-emerald-400 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700 transition hover:bg-emerald-50 disabled:opacity-40"
              >
                ⬇ Download Excel
              </button>
              {canWrite && (
                <button
                  type="button"
                  onClick={save}
                  disabled={saving || !dirtyCount}
                  className="rounded-full bg-amber-500 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400 disabled:opacity-50"
                >
                  {saving
                    ? "Saving…"
                    : `Save${dirtyCount ? ` (${dirtyCount})` : ""}`}
                </button>
              )}
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  {/* <th className={th}>#</th> */}
                  <th className={th}>SR.NO</th>
                  <th className={th}>Call Date</th>
                  <th className={`${th} bg-amber-50 text-amber-800`}>Stars</th>
                  <th className={`${th} bg-amber-50 text-amber-800`}>
                    Remark's
                  </th>
                  <th className={`${th} bg-red-50 text-center text-red-700`}>Negative</th>
                  {canWrite && <th className={th}>Save</th>}
                  <th className={th}>NAME</th>
                  <th className={th}>PHONE NO.</th>
                  <th className={th}>Check-In Date</th>
                  <th className={th}>Check-Out Date</th>
                  <th className={th}>GUEST STATUS</th>
                  <th className={th}>Reservation Stay Duration</th>
                  <th className={th}>Room Type</th>
                  <th className={th}>ROOM</th>
                  <th className={th}>Rate/Package Name</th>
                  <th className={`${th} text-right`}>Average Rate</th>
                  {isSuper && <th className={th} />}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {!loading && visible.length === 0 && (
                  <tr>
                    <td
                      colSpan={18}
                      className="px-4 py-10 text-center text-gray-400"
                    >
                      {rows.length
                        ? "No rows match the filter."
                        : "Nothing uploaded for this month yet."}
                    </td>
                  </tr>
                )}
                {visible.map((r, i) => {
                  const dirty = !!drafts[r._id];
                  const rating = valueOf(r, "rating");
                  const negative = Boolean(valueOf(r, "negative"));
                  const prev = visible[i - 1];
                  const newBatch =
                    prev &&
                    prev.uploadBatch &&
                    r.uploadBatch !== prev.uploadBatch;
                  return (
                    <tr
                      key={r._id}
                      className={`${
                        negative
                          ? "border-l-4 border-l-red-500 bg-red-50"
                          : dirty
                            ? "bg-amber-50/60"
                            : r.repeat
                              ? "bg-rose-100"
                              : ""
                      } ${newBatch ? "border-t-2 border-t-sky-200" : ""}`}
                      title={
                        newBatch
                          ? `Uploaded ${fmtDate(r.createdAt)}${r.fileName ? ` · ${r.fileName}` : ""}`
                          : undefined
                      }
                    >
                      {/* <td className={`${cell} text-xs text-gray-400`}>
                        {i + 1}
                      </td> */}
                      <td className={`${cell} tabular-nums text-gray-500`}>
                        {i + 1}
                      </td>
                      <td className={`${cell} whitespace-nowrap`}>
                        {r.date || r.ratedAt ? (
                          fmtDate(r.date || r.ratedAt)
                        ) : (
                          <span className="text-gray-400">Not called</span>
                        )}
                      </td>
                      <td className={`${cell} whitespace-nowrap`}>
                        {canWrite ? (
                          <div className="flex items-center gap-2">
                            <select
                              className={`${field} w-24 py-1.5`}
                              style={WHITE}
                              value={rating == null ? "" : String(rating)}
                              onChange={(e) =>
                                edit(r, "rating", e.target.value)
                              }
                            >
                              <option value="">—</option>
                              {RATING_OPTIONS.map((o) => (
                                <option key={o} value={o}>
                                  {o} ★
                                </option>
                              ))}
                            </select>
                            {rating != null && rating !== "" && (
                              <StarRating
                                value={rating}
                                size={14}
                                showValue={false}
                              />
                            )}
                          </div>
                        ) : (
                          <StarRating value={r.rating} size={14} />
                        )}
                      </td>
                      <td className={`${cell} min-w-[240px]`}>
                        {canWrite ? (
                          <textarea
                            rows={1}
                            className={`${field} min-h-[38px] resize-y py-1.5`}
                            style={WHITE}
                            placeholder="Remark"
                            value={valueOf(r, "remarks") || ""}
                            onChange={(e) => edit(r, "remarks", e.target.value)}
                          />
                        ) : (
                          r.remarks || <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className={`${cell} text-center`}>
                        {canWrite ? (
                          <input
                            type="checkbox"
                            className="h-4 w-4 cursor-pointer accent-red-600"
                            checked={negative}
                            onChange={(e) => edit(r, "negative", e.target.checked)}
                            aria-label={`Mark ${r.name}'s feedback as negative`}
                          />
                        ) : null}
                        {negative && (
                          <div className="mt-1 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold text-white">
                            NEGATIVE
                          </div>
                        )}
                      </td>
                      {canWrite && (
                        <td className={`${cell} whitespace-nowrap`}>
                          <button
                            type="button"
                            onClick={() => save(r._id)}
                            disabled={saving || !dirty}
                            className="rounded-lg bg-amber-500 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
                          >
                            Save
                          </button>
                        </td>
                      )}
                      <td
                        className={`${cell} whitespace-nowrap font-medium ${
                          r.repeat && !negative ? "text-rose-800" : ""
                        }`}
                      >
                        {r.name}
                        <RepeatBadge repeat={r.repeat} />
                      </td>
                      <td className={`${cell} whitespace-nowrap tabular-nums`}>
                        {r.phone || "—"}
                      </td>
                      <td className={`${cell} whitespace-nowrap`}>
                        {fmtDate(r.checkInDate)}
                      </td>
                      <td className={`${cell} whitespace-nowrap`}>
                        {fmtDate(r.checkOutDate)}
                      </td>
                      <td className={cell}>{r.guestStatus || "—"}</td>
                      <td
                        className={`${cell} whitespace-nowrap text-xs text-gray-600`}
                      >
                        {r.stayDuration || "—"}
                      </td>
                      <td className={cell}>{r.roomType || "—"}</td>
                      <td className={`${cell} tabular-nums`}>
                        {r.room || "—"}
                      </td>
                      <td className={cell}>{r.ratePackage || "—"}</td>
                      <td
                        className={`${cell} whitespace-nowrap text-right tabular-nums`}
                      >
                        {inr(r.averageRate)}
                      </td>
                      {isSuper && (
                        <td className={cell}>
                          <button
                            type="button"
                            onClick={() => removeRow(r)}
                            className="text-xs font-semibold text-rose-500 hover:text-rose-700"
                            title="Delete row (super-admin)"
                          >
                            ✕
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {canWrite && dirtyCount > 0 && (
            <div className="sticky bottom-3 mt-4 flex justify-end">
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="rounded-full bg-amber-500 px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 shadow-lg transition hover:bg-amber-400 disabled:opacity-50"
              >
                {saving
                  ? "Saving…"
                  : `Save ${dirtyCount} change${dirtyCount === 1 ? "" : "s"}`}
              </button>
            </div>
          )}
        </section>
      </div>
    </>
  );
};

export default Feedback;
